import PageHeading from "../../../components/desktop/PageHeading";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAuthStore } from "../../../store/authStore";
import { getCompanyThemes, saveCompanyThemes } from "../../../utils/companyTheme";
import { FiCheck, FiRotateCcw } from "react-icons/fi";
import {
    DEFAULT_PALETTE,
    HEX_RE,
    PALETTE_PRESETS,
    shadeOf,
    useThemeStore,
} from "../../../store/themeStore";
import "./Themesettings.css";

const gradientOf = ({ primary, deep, end }) =>
    `linear-gradient(135deg, ${deep} 0%, ${end || primary} 100%)`;

const defaultDrafts = () => ({
    dark: { ...DEFAULT_PALETTE, end: DEFAULT_PALETTE.primary },
    light: { ...DEFAULT_PALETTE, end: DEFAULT_PALETTE.primary },
});

const draftsFromThemes = (themes) => {
    const drafts = defaultDrafts();
    if (themes.length) {
        const first = themes[0];
        const fallback = { primary: first.primary_color, deep: first.gradient_start, end: first.gradient_end };
        drafts.dark = { ...fallback };
        drafts.light = { ...fallback };
    }
    themes.forEach(item => {
        drafts[item.theme] = {
            primary: item.primary_color,
            deep: item.gradient_start,
            end: item.gradient_end,
        };
    });
    return drafts;
};

const sameColour = (a, b) => a.toLowerCase() === b.toLowerCase();

// colour input + editable hex text, kept in sync
function ColourField({ label, hint, value, onChange }) {
    const [draft, setDraft] = useState(value);

    useEffect(() => setDraft(value), [value]);

    const commitDraft = (text) => {
        const hex = text.startsWith("#") ? text : `#${text}`;
        setDraft(hex);
        if (HEX_RE.test(hex)) onChange(hex.toLowerCase());
    };

    return (
        <div className="theme-colour-field">
            <div className="theme-colour-field-text">
                <span className="theme-colour-field-label">{label}</span>
                <span className="theme-colour-field-hint">{hint}</span>
            </div>

            <input
                type="text"
                className="theme-hex-input"
                aria-label={`${label} hex colour`}
                value={draft}
                maxLength={7}
                spellCheck={false}
                onChange={(e) => commitDraft(e.target.value.trim())}
                onBlur={() => setDraft(value)}
            />

            <input
                type="color"
                className="theme-colour-input"
                value={value}
                aria-label={label}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );
}

export default function Themesettings() {
    const { theme: currentTheme, setTheme, setCompanyThemes } = useThemeStore();
    const { user } = useAuthStore();
    const companyId = user?.company_id;
    const [theme, setSelectedTheme] = useState(currentTheme);
    const [drafts, setDrafts] = useState(defaultDrafts);
    const [savedDrafts, setSavedDrafts] = useState(defaultDrafts);
    const [exists, setExists] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [reload, setReload] = useState(0);
    const requestVersion = useRef(0);
    const saveLock = useRef(false);
    const savedMode = useRef(currentTheme);
    const palette = drafts[theme];
    const busy = isLoading || isSaving || !!loadError || !companyId;

    useEffect(() => {
        const version = ++requestVersion.current;
        saveLock.current = false;
        setIsSaving(false);
        setIsLoading(true);
        setLoadError("");
        if (!companyId) {
            setLoadError("Company ID not found");
            setIsLoading(false);
            return;
        }
        getCompanyThemes(companyId).then(themes => {
            if (version !== requestVersion.current) return;
            const loaded = draftsFromThemes(themes);
            setDrafts(loaded);
            setSavedDrafts(loaded);
            setExists(themes.length > 0);
            const mode = useThemeStore.getState().theme;
            savedMode.current = mode;
            setSelectedTheme(mode);
            setCompanyThemes(themes);
        }).catch(error => {
            if (version === requestVersion.current) setLoadError(error.message || "Unable to load theme");
        }).finally(() => {
            if (version === requestVersion.current) setIsLoading(false);
        });
        return () => { requestVersion.current += 1; };
    }, [companyId, reload, setCompanyThemes]);

    const setPalette = (changes) => setDrafts(previous => {
        const updated = { ...previous[theme], ...changes };
        return { dark: { ...updated }, light: { ...updated } };
    });

    const handleCancel = () => {
        setDrafts(savedDrafts);
        setSelectedTheme(savedMode.current);
    };

    const handleApply = async () => {
        if (busy || saveLock.current) return;
        saveLock.current = true;
        setIsSaving(true);
        const version = requestVersion.current;
        const themes = ["dark", "light"].map(mode => ({
            theme: mode,
            primary_color: palette.primary,
            gradient_start: palette.deep,
            gradient_end: palette.end,
        }));
        try {
            const saved = await saveCompanyThemes(companyId, themes, exists);
            if (version !== requestVersion.current) return;
            const loaded = draftsFromThemes(saved);
            setSavedDrafts(loaded);
            setDrafts(loaded);
            setExists(true);
            savedMode.current = theme;
            setCompanyThemes(saved);
            setTheme(theme);
            toast.success("Company theme applied successfully");
        } catch (error) {
            if (version === requestVersion.current) toast.error(error.message || "Unable to apply theme");
        } finally {
            if (version === requestVersion.current) {
                saveLock.current = false;
                setIsSaving(false);
            }
        }
    };

    const activePreset = PALETTE_PRESETS.find(
        (p) => sameColour(p.primary, palette.primary) && sameColour(p.deep, palette.deep) && sameColour(p.primary, palette.end)
    );

    const isDefault =
        sameColour(palette.primary, DEFAULT_PALETTE.primary) &&
        sameColour(palette.deep, DEFAULT_PALETTE.deep) &&
        sameColour(palette.end, DEFAULT_PALETTE.primary);

    return (
        <div className="theme-settings">

            <div className="theme-settings-head">
                <div>
                    <PageHeading>Theme Settings</PageHeading>
                    <p>Choose colours once, then Apply to use them in both light and dark mode.</p>
                </div>

                <button
                    type="button"
                    className="theme-reset-btn"
                    onClick={() => setPalette({ ...DEFAULT_PALETTE, end: DEFAULT_PALETTE.primary })}
                    disabled={isDefault || busy}
                >
                    <FiRotateCcw /> Reset to default
                </button>
            </div>

            {isLoading && <p role="status">Loading company theme...</p>}
            {loadError && <div className="theme-load-error" role="alert">
                {loadError}
                <button type="button" onClick={() => setReload(value => value + 1)}>Retry</button>
            </div>}

            <fieldset className="theme-settings-main" disabled={busy}>

                {/* Presets */}

                <section className="theme-panel">
                    <div className="theme-panel-head">
                        <h3>Colour presets</h3>
                        <span>{activePreset ? activePreset.name : "Custom"}</span>
                    </div>

                    <div className="theme-preset-grid">
                        {PALETTE_PRESETS.map((preset) => {
                            const active = preset === activePreset;

                            return (
                                <button
                                    key={preset.id}
                                    type="button"
                                    className={`theme-preset ${active ? "active" : ""}`}
                                    aria-pressed={active}
                                    onClick={() =>
                                        setPalette({ primary: preset.primary, deep: preset.deep, end: preset.primary })
                                    }
                                >
                                    <span
                                        className="theme-preset-swatch"
                                        style={{ background: gradientOf(preset) }}
                                    >
                                        {active && <FiCheck />}
                                    </span>
                                    <span className="theme-preset-name">{preset.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* Custom */}

                <section className="theme-panel">
                    <div className="theme-panel-head">
                        <h3>Custom gradient</h3>
                    </div>

                    <div
                        className="theme-gradient-bar"
                        style={{ background: gradientOf(palette) }}
                    />

                    <ColourField
                        label="Primary"
                        hint="Buttons, highlights, active icons"
                        value={palette.primary}
                        onChange={(primary) => setPalette({ primary })}
                    />

                    <ColourField
                        label="Gradient start"
                        hint="Dark end of the gradient and background glow"
                        value={palette.deep}
                        onChange={(deep) => setPalette({ deep })}
                    />

                    <ColourField
                        label="Gradient end"
                        hint="Colour at the bright end of the gradient"
                        value={palette.end}
                        onChange={(end) => setPalette({ end })}
                    />

                    <button
                        type="button"
                        className="theme-link-btn"
                        onClick={() => setPalette({ deep: shadeOf(palette.primary) })}
                    >
                        Match shade to primary
                    </button>
                </section>

            </fieldset>
            <div className="theme-settings-actions">
                <button type="button" className="theme-cancel-btn" onClick={handleCancel} disabled={busy}>Cancel</button>
                <button type="button" className="theme-apply-btn" onClick={handleApply} disabled={busy}>
                    {isSaving ? "Applying..." : "Apply"}
                </button>
            </div>
        </div>
    );
}
